import { useCallback, useMemo } from 'react';

import { useTable } from '@/components/data-table/core/context';
import type { FilterType } from '@/components/data-table/core/types/column.types';

interface UseFilterStateOptions {
  columnId: string;
  filterType: FilterType;
}

interface UseFilterStateResult<T> {
  /** Current filter value */
  value: T;
  /** Update filter value */
  setValue: (value: T | undefined) => void;
  /** Clear filter (set to undefined) */
  clear: () => void;
  /** Whether filter has an active (non-empty) value */
  isActive: boolean;
}

/**
 * Hook to manage filter state for a specific column.
 * Reads from columnFilters and writes via table.getColumn().setFilterValue().
 */
export const useFilterState = <T>({
  columnId,
  filterType,
}: UseFilterStateOptions): UseFilterStateResult<T> => {
  const { table, columnFilters } = useTable();

  // Get current value from columnFilters state
  const value = useMemo(() => {
    const filter = columnFilters.find((f) => f.id === columnId);
    return (filter?.value as T) ?? getDefaultValue<T>(filterType);
  }, [columnFilters, columnId, filterType]);

  // Set filter value via TanStack Table
  const setValue = useCallback(
    (newValue: T | undefined) => {
      table.getColumn(columnId)?.setFilterValue(newValue);
    },
    [table, columnId]
  );

  // Clear filter
  const clear = useCallback(() => {
    table.getColumn(columnId)?.setFilterValue(undefined);
  }, [table, columnId]);

  // Determine if filter is active
  const isActive = useMemo(() => {
    return isFilterActive(value, filterType);
  }, [value, filterType]);

  return { value, setValue, clear, isActive };
};

// Helper to get default empty value for each filter type
const getDefaultValue = <T>(filterType: FilterType): T => {
  switch (filterType) {
    case 'search':
    case 'select':
      return '' as T;
    case 'multi-select':
      return [] as T;
    case 'date-range':
      return {} as T;
    case 'number-range':
      return {} as T;
    default:
      return '' as T;
  }
};

// Helper to determine if a filter value is "active" (non-empty)
const isFilterActive = (value: unknown, filterType: FilterType): boolean => {
  switch (filterType) {
    case 'search':
    case 'select':
      return typeof value === 'string' && value.length > 0;
    case 'multi-select':
      return Array.isArray(value) && value.length > 0;
    case 'date-range': {
      const v = value as { from?: Date; to?: Date; equals?: Date } | undefined;
      return !!(v?.from || v?.to || v?.equals);
    }
    case 'number-range': {
      const v = value as { equals?: number; greaterThan?: number; lessThan?: number } | undefined;
      return v?.equals !== undefined || v?.greaterThan !== undefined || v?.lessThan !== undefined;
    }
    default:
      return false;
  }
};
